<template>
  <div class="DatePicker">
    <div class="DatePicker__Wrapper">
      <options
        :selected="selected"
        @onOptionClick="handleSelection"
        :firstAvailableDate="firstAvailableDate"
      />
      <vc-date-picker
        :masks="masks"
        :max-date="maxDate"
        :min-date="minDate"
        @dayclick="handleDayClick"
        is-inline
        mode="range"
        show-caps
        v-model="date"
      >
      </vc-date-picker>
    </div>
    <div class="DatePicker__Footer">
      <Button color="blue" @onClick="onApply">{{
        $t('date-picker.see')
      }}</Button>
    </div>
  </div>
</template>

<script>
import { Button } from 'daas-components';

import { LAST_SEVEN_DAYS } from '@/constants';
import calculateDate, {
  getLastSevenDays,
  isFirstDay,
  getToday,
} from '@/utils/calculateDate';
import EventBus from '@/utils/eventBus';

import Options from './Options.vue';

export default {
  name: 'DatePicker',
  components: {
    Options,
    Button,
  },
  data() {
    return {
      firstAvailableDate: null,
      selected: null,
      date: isFirstDay() ? getToday() : getLastSevenDays(),
      masks: { weekdays: 'WWW' },
    };
  },
  computed: {
    minDate() {
      return this.firstAvailableDate;
    },
    maxDate() {
      return new Date();
    },
  },
  methods: {
    setDate(date) {
      this.date = date;
    },
    getDate(option) {
      return calculateDate(this.firstAvailableDate)(option);
    },
    onApply() {
      const date = this.getDate(this.selected);
      EventBus.$emit('ON_DATE_APPLY', date, this.selected);
    },
    handleSelection(optionId) {
      this.selected = optionId;
      this.setDate(this.getDate(optionId));
    },
    handleDayClick(date) {
      this.date = date.date;
    },
  },
  mounted() {
    this.selected = this.defaultSelected;
  },
  props: {
    withToggler: {
      type: Boolean,
      required: false,
      default: false,
    },
    defaultSelected: {
      type: String,
      required: false,
      default: LAST_SEVEN_DAYS,
    },
  },
};
</script>

<style lang="scss">
.DatePicker {
  background: $white;
  border-radius: rem(4);
  box-shadow: 0 rem(3) rem(10) 0 rgba(0, 0, 0, 0.16);
  padding: rem(10) rem(15);
  position: relative;

  &::before {
    border-bottom: rem(7) solid $white;
    border-left: rem(7) solid transparent;
    border-right: rem(7) solid transparent;
    content: '';
    position: absolute;
    right: rem(7);
    top: -(rem(7));
    @include z-index(DatePickerArrow);
  }

  &__Wrapper {
    display: flex;

    &__Title {
      @include font-size(18);
      color: $primary;
      font-weight: 400;
      opacity: 0.5;
      text-align: center;
      text-transform: capitalize;
    }

    .vc-day {
      margin: rem(2) 0;
    }

    .vc-container {
      border: none;
      margin: rem(10) rem(10) rem(10) rem(25);
    }

    .vc-highlight.vc-rounded-full,
    .vc-highlight.vc-highlight-base-middle,
    .vc-highlight.vc-highlight-base-start,
    .vc-highlight.vc-highlight-base-end {
      background: $white-500;
      border-radius: 0;
      height: 100%;
      width: 100%;
    }

    .vc-arrow {
      color: $primary;
      width: rem(6);
      opacity: 0.58;
    }

    .vc-weekday {
      @include font-size(10);
      margin: rem(15) 0 rem(2) 0;
      opacity: 0.5;
      text-transform: capitalize;
    }

    .vc-day-content {
      font-size: rem(10);
      color: $primary;
    }

    .vc-pointer-events-none {
      color: $blue-200;
    }
  }

  &__Footer {
    align-items: baseline;
    display: flex;
    width: 100%;

    &--spaced {
      justify-content: space-between;
    }

    &--end {
      justify-content: flex-end;
    }

    &__Toggler {
      align-items: center;
      display: flex;
      min-width: rem(10);

      p {
        font-size: rem(11);
        font-weight: 700;
        margin-right: rem(16);
      }
    }

    .Button {
      font-size: rem(10);
      height: rem(30);
      padding: 0;
      width: rem(70);
    }
  }
}
</style>
